import { confirmToken as apiConfirmToken, deleteAccount, logout, requestLink as apiRequestLink } from './api.js'

/**
 * The account section of the panel.
 *
 * Signed out it is one email field: routing is the primary action here, so
 * sign-in stays a quiet row under it rather than a competing form.
 *
 * After a request it says "check your email" and nothing else. It must never
 * report whether the address had an account -- that turns a public map into
 * an address-membership oracle. The server answers 202 either way; so does
 * this.
 */
export function renderAuth(container, {
  user,
  onChange,
  requestLink = apiRequestLink,
  signOut = logout,
  removeAccount = deleteAccount,
}) {
  // paint() runs on every route response, twice per refresh and again when
  // the session lands. Re-rendering unconditionally wiped a half-typed email,
  // dropped focus, and erased the "check your email" confirmation. Nothing
  // drawn here depends on anything but who is signed in, so when that has not
  // changed the existing DOM is left exactly as the user left it.
  const key = user ? `in:${user.email}` : 'out'
  if (container.dataset.authKey === key && container.firstChild) return
  container.dataset.authKey = key

  container.replaceChildren()
  container.classList.add('account')

  const label = document.createElement('p')
  label.className = 'section-label'
  label.textContent = 'Account'
  container.append(label)

  const status = document.createElement('p')
  status.className = 'note'

  if (user) {
    const row = document.createElement('div')
    row.className = 'account-row'

    const who = document.createElement('span')
    who.className = 'account-email'
    who.textContent = user.email
    who.title = user.email

    const out = document.createElement('button')
    out.type = 'button'
    out.className = 'link-button'
    out.textContent = 'Sign out'
    out.addEventListener('click', async () => {
      out.disabled = true
      try {
        await signOut()
        onChange(null)
      } catch (error) {
        out.disabled = false
        fail(status, error)
      }
    })

    row.append(who, out)
    container.append(row)

    const remove = document.createElement('button')
    remove.type = 'button'
    remove.className = 'link-button link-button--danger'
    remove.textContent = 'Delete account'
    remove.addEventListener('click', async () => {
      // Deleting takes the saved places with it, so it asks first.
      if (typeof window !== 'undefined' && window.confirm
        && !window.confirm('Delete your account and all saved places?')) return
      remove.disabled = true
      try {
        await removeAccount()
        onChange(null)
      } catch (error) {
        remove.disabled = false
        fail(status, error)
      }
    })
    container.append(remove, status)
    return
  }

  const form = document.createElement('form')
  form.className = 'account-form'
  form.noValidate = true

  const input = document.createElement('input')
  input.type = 'email'
  input.name = 'email'
  input.className = 'search-input account-input'
  input.required = true
  input.autocomplete = 'email'
  input.placeholder = 'you@example.com'
  input.setAttribute('aria-label', 'Email address')

  const submit = document.createElement('button')
  submit.type = 'submit'
  submit.className = 'account-submit'
  submit.textContent = 'Send link'

  const hint = document.createElement('p')
  hint.className = 'hint'
  hint.textContent = 'Sign in to save places. No password -- we email you a link.'

  form.addEventListener('submit', async (event) => {
    event.preventDefault()
    const email = input.value.trim()
    if (!email) {
      status.className = 'error'
      status.textContent = 'Enter an email address.'
      return
    }
    submit.disabled = true
    try {
      await requestLink(email)
      // Deliberately identical for a known and an unknown address.
      form.remove()
      hint.remove()
      status.className = 'note'
      status.textContent = `Check your email for a sign-in link.`
    } catch (error) {
      submit.disabled = false
      fail(status, error)
    }
  })

  form.append(input, submit)
  container.append(form, hint, status)
}

function fail(status, error) {
  status.className = 'error'
  status.textContent = error?.message ?? 'Something went wrong.'
}

/**
 * The /auth/callback page.
 *
 * It renders a button and stops. It must NOT confirm on load: corporate mail
 * scanners fetch every link in an email before a human ever sees it, so a
 * page that signs in on GET burns the token -- and hands the session to the
 * scanner. Only the click confirms.
 */
export function renderCallback(container, {
  token,
  confirmToken = apiConfirmToken,
  onSignedIn,
}) {
  container.replaceChildren()
  container.classList.add('callback')

  const title = document.createElement('h1')
  title.textContent = 'trxmap'

  const status = document.createElement('p')
  status.className = 'note'

  if (!token) {
    status.className = 'error'
    status.textContent = 'This sign-in link is missing its token. Request a new one.'
    container.append(title, status)
    return
  }

  status.textContent = 'Confirm that it was you who asked to sign in.'

  const button = document.createElement('button')
  button.type = 'button'
  button.className = 'account-submit account-submit--wide'
  button.textContent = 'Sign in'
  button.addEventListener('click', async () => {
    button.disabled = true
    status.className = 'note'
    status.textContent = 'Signing in...'
    try {
      await confirmToken(token)
      onSignedIn()
    } catch (error) {
      button.disabled = false
      status.className = 'error'
      status.textContent = `${error?.message ?? 'That did not work'} — the link may have expired or already been used.`
    }
  })

  container.append(title, status, button)
}
